import { Cluster } from '@cast/types';
import { usePath } from '@cast/utils';

import { useIsClusterDemoMode } from '@cast/infrastructure-demo';
import { PRODUCT_BASE_PATHS } from '@cast/console-constants';
import {
  ClusterProvider,
  isClusterManaged,
} from '@cast/shared-cluster-context-data-access';
import {
  RADAR_NAV_TITLE,
  useShouldLimitConsoleForReporting,
} from '@cast/shared-trial-ui';
import { isDisconnectedForMonth } from 'features/cluster';
import {
  makeAllocationGroupsCostMonitoringLink,
  makeClusterCostMonitoringLink,
  makeCostComparisonLink,
  makeNamespacesCostMonitoringLink,
  makeSavingsReportLink,
  makeWorkloadsCostMonitoringLink,
} from 'features/cluster/cost-report';
import { useWoopInstallationStatus } from 'features/workload-optimization/hooks/useWoopInstallationStatus';

import { SidebarItem } from '../../_components/SidebarItem';

const CostMonitoringClusterSidebarContentInner = ({
  cluster,
}: {
  cluster: Cluster;
}) => {
  const isClusterDemoMode = useIsClusterDemoMode();
  const limitAutomationConsole = useShouldLimitConsoleForReporting();
  const basePath = PRODUCT_BASE_PATHS.automation;
  const isDisconnected = isDisconnectedForMonth(cluster);

  const isManaged = isClusterManaged(cluster);
  const { isInstalled: isWoopInstalled } = useWoopInstallationStatus();

  const clusterLink = usePath(() =>
    makeClusterCostMonitoringLink({ clusterId: cluster.id })
  );
  const workloadsLink = usePath(() =>
    makeWorkloadsCostMonitoringLink({ clusterId: cluster.id })
  );
  const namespacesLink = usePath(() =>
    makeNamespacesCostMonitoringLink({ clusterId: cluster.id })
  );
  const allocationGroupsLink = usePath(() =>
    makeAllocationGroupsCostMonitoringLink({ clusterId: cluster.id })
  );
  const savingsReportLink = usePath(() =>
    makeSavingsReportLink({ clusterId: cluster.id })
  );
  const costComparisonLink = usePath(() =>
    makeCostComparisonLink({ clusterId: cluster.id })
  );

  return (
    <SidebarItem
      title={RADAR_NAV_TITLE}
      testId="cluster-cost-monitoring"
      url={`${basePath}/external-clusters/${cluster.id}/cost-report`}
      rules={[['view', 'ClusterCostMonitoring']]}
      rulesConditions={{ clusterId: cluster.id }}
      disabled={isDisconnected}
    >
      <SidebarItem title="Cluster" url={clusterLink} allowedInReadOnlyOrg />
      {!limitAutomationConsole && (
        <SidebarItem title="Workloads" url={workloadsLink} />
      )}
      <SidebarItem title="Namespaces" url={namespacesLink} />
      <SidebarItem title="Allocation groups" url={allocationGroupsLink} />
      {!limitAutomationConsole && (
        <SidebarItem
          title="Savings report"
          url={savingsReportLink}
          disabled={!isManaged && !isWoopInstalled && !isClusterDemoMode}
          rules={[['view', 'ValueReport']]}
        />
      )}
      <SidebarItem
        title="Cost comparison"
        url={costComparisonLink}
        rulesConditions={{ clusterId: cluster.id }}
      />
    </SidebarItem>
  );
};

export const CostMonitoringClusterSidebarItems = ({
  cluster,
}: {
  cluster: Cluster;
}) => {
  return (
    <ClusterProvider cluster={cluster} isLoading={false}>
      <CostMonitoringClusterSidebarContentInner cluster={cluster} />
    </ClusterProvider>
  );
};
